import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { t } from 'i18next';
import axios from '../../config/axios';
import Toast from '../../shared/toast/Toast';
import { ResponseResult } from '../../utils/interfaces/http/ResponseResult';
import { ApplicationTypeEnum } from '../../utils/interfaces/screening-questions/ScreeningQuestionsInterfaces';
import {
    CreateEvaluationTemplateRequest,
    EvaluationSummariesBatchRequest,
    EvaluationTemplateDetail,
    EvaluationTemplateListItem,
    EvaluationTemplateStatusEnum,
    InterviewEvaluationItem,
    InterviewEvaluationSummary,
    InterviewEvaluationsStoreInterface,
    JobEvaluationAttachment,
    SubmitEvaluationRequest,
    UpdateEvaluationRequest,
    UpdateEvaluationTemplateRequest,
    UpsertJobEvaluationAttachmentRequest,
} from '../../utils/interfaces/interview-evaluations/InterviewEvaluationInterfaces';
import { RootState } from '../store';
import { forceSetApplicantsList, setKanbanColumnApplicants, updateSelectedApplicant } from '../vetting/vetting';
import { ApplicantListItem } from '../../utils/interfaces/applicants/ApplicantListItem';

const appKey = 'interviewEvaluations';
const templatesUrl = 'pa/ft/evaluation-templates';
const baseUrl = 'pa/ft';

/** Normalize list/detail payloads so status + seeded flag render reliably across API shapes. */
const normalizeTemplateListItem = (template: EvaluationTemplateListItem | Record<string, any>): EvaluationTemplateListItem => {
    const raw = template as Record<string, any>;
    const statusRaw = String(raw.status || '').toLowerCase();
    const status =
        statusRaw === EvaluationTemplateStatusEnum.ARCHIVED
            ? EvaluationTemplateStatusEnum.ARCHIVED
            : EvaluationTemplateStatusEnum.ACTIVE;

    return {
        ...(template as EvaluationTemplateListItem),
        status,
        isSeeded: !!(raw.isSeeded ?? raw.is_seeded ?? raw.isDefault ?? raw.is_default),
        description: raw.description || undefined,
        attachedJobs: raw.attachedJobs || raw.attached_jobs || [],
    };
};

const normalizeTemplateDetail = (template: EvaluationTemplateDetail | Record<string, any>): EvaluationTemplateDetail => {
    const listFields = normalizeTemplateListItem(template);
    const raw = template as Record<string, any>;
    return {
        ...(template as EvaluationTemplateDetail),
        status: listFields.status,
        isSeeded: listFields.isSeeded,
        description: listFields.description,
        criteria: Array.isArray(raw.criteria) ? raw.criteria : [],
    };
};

/** Normalize attachment payloads (camelCase or snake_case) so stage chips bind reliably. */
const normalizeJobAttachment = (
    attachment: JobEvaluationAttachment | Record<string, any> | null | undefined,
): JobEvaluationAttachment | null => {
    if (!attachment) return null;
    const raw = attachment as Record<string, any>;
    const templateId = raw.templateId || raw.template_id;
    const stageId = raw.stageId || raw.stage_id;
    if (!templateId || !stageId) return null;

    return {
        templateId,
        templateName: raw.templateName || raw.template_name || '',
        stageId,
        stageName: raw.stageName ?? raw.stage_name,
        currentVersion: raw.currentVersion ?? raw.current_version ?? 1,
        attachedAt: raw.attachedAt || raw.attached_at || new Date().toISOString(),
    };
};

const resolveAttachmentTemplateName = (
    state: InterviewEvaluationsStoreInterface,
    templateId?: string,
    fallbackName?: string,
) =>
    fallbackName ||
    state.templates.find((tpl) => tpl.id === templateId)?.name ||
    state.jobAttachment?.templateName ||
    '';

/** Snapshot taken before optimistic attachment writes; restored on reject. */
let jobAttachmentRollback: JobEvaluationAttachment | null | undefined;

/**
 * Mirrors an evaluation summary onto the vetting store (selected applicant, list, kanban).
 * Why: the gate and card badges read evaluationSummary from those places; after submit/
 * summary fetch we must update them or the next move stays blocked / UI stays stale.
 */
const syncApplicantEvaluationSummary = (
    dispatch: (action: any) => void,
    getState: () => RootState,
    applicationExternalId: string,
    summary: InterviewEvaluationSummary,
) => {
    const { selectedApplicant, applicantsList, kanbanColumnsData } = getState().vetting;

    if (selectedApplicant?.externalId === applicationExternalId) {
        dispatch(updateSelectedApplicant({ ...selectedApplicant, evaluationSummary: summary }));
    }

    if (applicantsList?.some((a) => a.externalId === applicationExternalId)) {
        dispatch(
            forceSetApplicantsList(
                applicantsList.map((a) =>
                    a.externalId === applicationExternalId ? { ...a, evaluationSummary: summary } : a,
                ),
            ),
        );
    }

    Object.keys(kanbanColumnsData || {}).forEach((columnId) => {
        const applicants = kanbanColumnsData[columnId]?.applicants as ApplicantListItem[] | undefined;
        if (!applicants?.some((a) => a.externalId === applicationExternalId)) return;
        dispatch(
            setKanbanColumnApplicants({
                columnId,
                applicants: applicants.map((a) =>
                    a.externalId === applicationExternalId ? { ...a, evaluationSummary: summary } : a,
                ),
            }),
        );
    });
};

const initialState: InterviewEvaluationsStoreInterface = {
    templates: [],
    selectedTemplate: null,
    isLoadingTemplates: false,
    isLoadingTemplateDetail: false,
    isSavingTemplate: false,

    jobAttachment: null,
    isLoadingJobAttachment: false,
    isSavingJobAttachment: false,
    attachmentEpoch: 0,

    evaluations: [],
    evaluationSummary: null,
    isLoadingEvaluations: false,
    isSubmittingEvaluation: false,
    isDeletingEvaluation: false,

    showSubmitPanel: false,
    editingEvaluationId: null,
};

// ---- Templates ----

export const listEvaluationTemplates = createAsyncThunk(
    `${appKey}/listEvaluationTemplates`,
    async (status: EvaluationTemplateStatusEnum | 'all' = 'all', { rejectWithValue }) => {
        try {
            const response = await axios.get<ResponseResult<{ templates: EvaluationTemplateListItem[] }>>(
                templatesUrl,
                { params: { status } },
            );
            return (response.data.data.templates || []).map(normalizeTemplateListItem);
        } catch (error: any) {
            return rejectWithValue({ message: error?.message, code: error?.code });
        }
    },
);

export const getEvaluationTemplate = createAsyncThunk(
    `${appKey}/getEvaluationTemplate`,
    async (id: string, { rejectWithValue }) => {
        try {
            const response = await axios.get<ResponseResult<EvaluationTemplateDetail>>(`${templatesUrl}/${id}`);
            return normalizeTemplateDetail(response.data.data);
        } catch (error: any) {
            return rejectWithValue({ message: error?.message, code: error?.code });
        }
    },
);

export const createEvaluationTemplate = createAsyncThunk(
    `${appKey}/createEvaluationTemplate`,
    async (body: CreateEvaluationTemplateRequest, { rejectWithValue }) => {
        try {
            const response = await axios.post<ResponseResult<EvaluationTemplateDetail>>(templatesUrl, body);
            return normalizeTemplateDetail(response.data.data);
        } catch (error: any) {
            return rejectWithValue({ message: error?.message, code: error?.code, details: error?.details });
        }
    },
);

export const updateEvaluationTemplate = createAsyncThunk(
    `${appKey}/updateEvaluationTemplate`,
    async ({ id, body }: { id: string; body: UpdateEvaluationTemplateRequest }, { rejectWithValue }) => {
        try {
            const response = await axios.put<ResponseResult<EvaluationTemplateDetail>>(`${templatesUrl}/${id}`, body);
            return normalizeTemplateDetail(response.data.data);
        } catch (error: any) {
            return rejectWithValue({ message: error?.message, code: error?.code, details: error?.details });
        }
    },
);

export const duplicateEvaluationTemplate = createAsyncThunk(
    `${appKey}/duplicateEvaluationTemplate`,
    async (id: string, { rejectWithValue }) => {
        try {
            const response = await axios.post<ResponseResult<EvaluationTemplateDetail>>(`${templatesUrl}/${id}/duplicate`);
            return normalizeTemplateDetail(response.data.data);
        } catch (error: any) {
            return rejectWithValue({ message: error?.message, code: error?.code });
        }
    },
);

export const archiveEvaluationTemplate = createAsyncThunk(
    `${appKey}/archiveEvaluationTemplate`,
    async (id: string, { rejectWithValue }) => {
        try {
            const response = await axios.post<ResponseResult<EvaluationTemplateListItem>>(`${templatesUrl}/${id}/archive`);
            return normalizeTemplateListItem({
                ...response.data.data,
                status: EvaluationTemplateStatusEnum.ARCHIVED,
            });
        } catch (error: any) {
            return rejectWithValue({ message: error?.message, code: error?.code });
        }
    },
);

export const restoreEvaluationTemplate = createAsyncThunk(
    `${appKey}/restoreEvaluationTemplate`,
    async (id: string, { rejectWithValue }) => {
        try {
            const response = await axios.post<ResponseResult<EvaluationTemplateListItem>>(`${templatesUrl}/${id}/restore`);
            return normalizeTemplateListItem({
                ...response.data.data,
                status: EvaluationTemplateStatusEnum.ACTIVE,
            });
        } catch (error: any) {
            return rejectWithValue({ message: error?.message, code: error?.code });
        }
    },
);

export const deleteEvaluationTemplate = createAsyncThunk(
    `${appKey}/deleteEvaluationTemplate`,
    async (id: string, { rejectWithValue }) => {
        try {
            await axios.delete<ResponseResult<null>>(`${templatesUrl}/${id}`);
            return id;
        } catch (error: any) {
            return rejectWithValue({ message: error?.message, code: error?.code });
        }
    },
);

// ---- Job attachment ----

export const getJobEvaluationAttachment = createAsyncThunk(
    `${appKey}/getJobEvaluationAttachment`,
    async (jobPostExternalId: string, { getState, rejectWithValue }) => {
        const epochAtStart = (getState() as RootState).interviewEvaluations.attachmentEpoch;
        try {
            const response = await axios.get<ResponseResult<JobEvaluationAttachment>>(
                `${baseUrl}/jobs/${jobPostExternalId}/evaluation-attachment`,
            );
            return { data: normalizeJobAttachment(response.data.data), epochAtStart };
        } catch (error: any) {
            // No attachment / no access is a valid empty state (404 / FT-103 / 403)
            if (
                error?.code === 'FT-103' ||
                error?.statusCode === 404 ||
                error?.status === 404 ||
                error?.statusCode === 403 ||
                error?.status === 403 ||
                error?.message === 'Forbidden'
            ) {
                return { data: null, epochAtStart };
            }
            return rejectWithValue({ message: error?.message, code: error?.code });
        }
    },
);

export const upsertJobEvaluationAttachment = createAsyncThunk(
    `${appKey}/upsertJobEvaluationAttachment`,
    async (
        { jobPostExternalId, body }: { jobPostExternalId: string; body: UpsertJobEvaluationAttachmentRequest },
        { rejectWithValue },
    ) => {
        try {
            const response = await axios.put<ResponseResult<JobEvaluationAttachment>>(
                `${baseUrl}/jobs/${jobPostExternalId}/evaluation-attachment`,
                body,
            );
            return normalizeJobAttachment(response.data.data);
        } catch (error: any) {
            return rejectWithValue({ message: error?.message, code: error?.code });
        }
    },
);

export const deleteJobEvaluationAttachment = createAsyncThunk(
    `${appKey}/deleteJobEvaluationAttachment`,
    async (jobPostExternalId: string, { rejectWithValue }) => {
        try {
            await axios.delete<ResponseResult<null>>(`${baseUrl}/jobs/${jobPostExternalId}/evaluation-attachment`);
            return null;
        } catch (error: any) {
            return rejectWithValue({ message: error?.message, code: error?.code });
        }
    },
);

// ---- Evaluations ----

export const listApplicationEvaluations = createAsyncThunk(
    `${appKey}/listApplicationEvaluations`,
    async (
        { applicationExternalId, applicationType }: { applicationExternalId: string; applicationType: ApplicationTypeEnum },
        { rejectWithValue },
    ) => {
        try {
            const response = await axios.get<ResponseResult<InterviewEvaluationItem[]>>(
                `${baseUrl}/applications/${applicationExternalId}/evaluations`,
                { params: { applicationType } },
            );
            return response.data.data || [];
        } catch (error: any) {
            // Recruiter (or other restricted roles) may get 403 — show empty state, not an error toast
            if (error?.statusCode === 403 || error?.status === 403 || error?.message === 'Forbidden') {
                return [];
            }
            return rejectWithValue({ message: error?.message, code: error?.code });
        }
    },
);

export const getApplicationEvaluationSummary = createAsyncThunk(
    `${appKey}/getApplicationEvaluationSummary`,
    async (
        { applicationExternalId, applicationType }: { applicationExternalId: string; applicationType: ApplicationTypeEnum },
        { rejectWithValue, dispatch, getState },
    ) => {
        try {
            const response = await axios.get<ResponseResult<InterviewEvaluationSummary>>(
                `${baseUrl}/applications/${applicationExternalId}/evaluations/summary`,
                { params: { applicationType } },
            );
            const summary = response.data.data;

            // Keep selected applicant / list / kanban in sync so badges and gate checks aren't stale
            if (summary) {
                syncApplicantEvaluationSummary(dispatch, getState as () => RootState, applicationExternalId, summary);
            }

            return summary;
        } catch (error: any) {
            // Restricted roles may get 403 — treat as no summary (empty evaluations UI)
            if (error?.statusCode === 403 || error?.status === 403 || error?.message === 'Forbidden') {
                return null;
            }
            return rejectWithValue({ message: error?.message, code: error?.code });
        }
    },
);

export const getEvaluationSummariesBatch = createAsyncThunk(
    `${appKey}/getEvaluationSummariesBatch`,
    async (body: EvaluationSummariesBatchRequest, { rejectWithValue }) => {
        try {
            const response = await axios.post<ResponseResult<Record<string, InterviewEvaluationSummary | null>>>(
                `${baseUrl}/evaluations/summaries`,
                body,
            );
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue({ message: error?.message, code: error?.code });
        }
    },
);

export const submitEvaluation = createAsyncThunk(
    `${appKey}/submitEvaluation`,
    async (
        {
            applicationExternalId,
            applicationType,
            body,
        }: {
            applicationExternalId: string;
            applicationType: ApplicationTypeEnum;
            body: SubmitEvaluationRequest;
        },
        { rejectWithValue, dispatch, getState },
    ) => {
        try {
            const response = await axios.post<ResponseResult<InterviewEvaluationItem>>(
                `${baseUrl}/applications/${applicationExternalId}/evaluations`,
                body,
                { params: { applicationType } },
            );
            const evaluation = response.data.data;

            // Optimistically bump evaluationSummary on vetting/kanban state so a follow-up
            // stage move right after submit isn't still blocked by a stale count of 0.
            // Accurate summary is still refreshed via getApplicationEvaluationSummary afterward.
            const state = getState() as RootState;
            const prevSummary =
                state.interviewEvaluations.evaluationSummary ||
                (state.vetting.selectedApplicant?.externalId === applicationExternalId
                    ? state.vetting.selectedApplicant.evaluationSummary
                    : null);
            const nextSummary: InterviewEvaluationSummary = {
                latestVerdict: evaluation.verdict,
                evaluationsCount: (prevSummary?.evaluationsCount || 0) + 1,
                distinctEvaluators: Math.max((prevSummary?.distinctEvaluators || 0) + 1, 1),
                myLockedEvaluationId: evaluation.id,
            };
            syncApplicantEvaluationSummary(dispatch, getState as () => RootState, applicationExternalId, nextSummary);

            return evaluation;
        } catch (error: any) {
            return rejectWithValue({ message: error?.message, code: error?.code, details: error?.details });
        }
    },
);

export const updateEvaluation = createAsyncThunk(
    `${appKey}/updateEvaluation`,
    async (
        { evaluationExternalId, body }: { evaluationExternalId: string; body: UpdateEvaluationRequest },
        { rejectWithValue },
    ) => {
        try {
            const response = await axios.patch<ResponseResult<InterviewEvaluationItem>>(
                `${baseUrl}/evaluations/${evaluationExternalId}`,
                body,
            );
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue({ message: error?.message, code: error?.code });
        }
    },
);

export const deleteEvaluation = createAsyncThunk(
    `${appKey}/deleteEvaluation`,
    async (evaluationExternalId: string, { rejectWithValue }) => {
        try {
            await axios.delete<ResponseResult<null>>(`${baseUrl}/evaluations/${evaluationExternalId}`);
            return evaluationExternalId;
        } catch (error: any) {
            return rejectWithValue({ message: error?.message, code: error?.code });
        }
    },
);

export const exportEvaluationPdf = createAsyncThunk(
    `${appKey}/exportEvaluationPdf`,
    async (evaluationExternalId: string, { rejectWithValue }) => {
        try {
            const response = await axios.get(`${baseUrl}/evaluations/${evaluationExternalId}/pdf`, {
                responseType: 'blob',
            });
            return response.data as Blob;
        } catch (error: any) {
            return rejectWithValue({ message: error?.message, code: error?.code });
        }
    },
);

const interviewEvaluationsSlice = createSlice({
    name: appKey,
    initialState,
    reducers: {
        clearSelectedTemplate: (state) => {
            state.selectedTemplate = null;
        },
        setShowSubmitEvaluationPanel: (state, action: PayloadAction<boolean>) => {
            state.showSubmitPanel = action.payload;
            if (!action.payload) state.editingEvaluationId = null;
        },
        setEditingEvaluationId: (state, action: PayloadAction<string | null>) => {
            state.editingEvaluationId = action.payload;
        },
        clearEvaluationsState: (state) => {
            state.evaluations = [];
            state.evaluationSummary = null;
            state.showSubmitPanel = false;
            state.editingEvaluationId = null;
        },
        clearJobAttachment: (state) => {
            state.jobAttachment = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(listEvaluationTemplates.pending, (state) => {
                state.isLoadingTemplates = true;
            })
            .addCase(listEvaluationTemplates.fulfilled, (state, action) => {
                state.isLoadingTemplates = false;
                state.templates = action.payload;
            })
            .addCase(listEvaluationTemplates.rejected, (state, action: any) => {
                state.isLoadingTemplates = false;
                Toast.Error({ content: action.payload?.message || t('error') });
            })

            .addCase(getEvaluationTemplate.pending, (state) => {
                state.isLoadingTemplateDetail = true;
            })
            .addCase(getEvaluationTemplate.fulfilled, (state, action) => {
                state.isLoadingTemplateDetail = false;
                state.selectedTemplate = action.payload;
            })
            .addCase(getEvaluationTemplate.rejected, (state, action: any) => {
                state.isLoadingTemplateDetail = false;
                Toast.Error({ content: action.payload?.message || t('error') });
            })

            .addCase(createEvaluationTemplate.pending, (state) => {
                state.isSavingTemplate = true;
            })
            .addCase(createEvaluationTemplate.fulfilled, (state, action) => {
                state.isSavingTemplate = false;
                state.selectedTemplate = action.payload;
                Toast.Success({ content: t('eval_template_created') });
            })
            .addCase(createEvaluationTemplate.rejected, (state, action: any) => {
                state.isSavingTemplate = false;
                Toast.Error({ content: action.payload?.message || t('error') });
            })

            .addCase(updateEvaluationTemplate.pending, (state) => {
                state.isSavingTemplate = true;
            })
            .addCase(updateEvaluationTemplate.fulfilled, (state, action) => {
                state.isSavingTemplate = false;
                state.selectedTemplate = action.payload;
                Toast.Success({ content: t('eval_template_updated') });
            })
            .addCase(updateEvaluationTemplate.rejected, (state, action: any) => {
                state.isSavingTemplate = false;
                Toast.Error({ content: action.payload?.message || t('error') });
            })

            .addCase(duplicateEvaluationTemplate.fulfilled, (state, action) => {
                Toast.Success({ content: t('eval_template_duplicated') });
                if (action.payload) {
                    state.templates = [
                        {
                            id: action.payload.id,
                            name: action.payload.name,
                            description: action.payload.description,
                            status: action.payload.status,
                            isSeeded: action.payload.isSeeded,
                            currentVersion: action.payload.currentVersion,
                            criteriaCount: (action.payload.criteria || []).filter((c) => c.type !== 'section').length,
                            attachedJobs: [],
                            updatedAt: action.payload.updatedAt,
                        },
                        ...state.templates,
                    ];
                }
            })
            .addCase(duplicateEvaluationTemplate.rejected, (_state, action: any) => {
                Toast.Error({ content: action.payload?.message || t('error') });
            })

            .addCase(archiveEvaluationTemplate.fulfilled, (state, action) => {
                const updated = action.payload;
                state.templates = state.templates.map((t) => (t.id === updated.id ? { ...t, ...updated } : t));
                Toast.Success({ content: t('eval_template_archived') });
            })
            .addCase(archiveEvaluationTemplate.rejected, (_state, action: any) => {
                Toast.Error({ content: action.payload?.message || t('error') });
            })

            .addCase(restoreEvaluationTemplate.fulfilled, (state, action) => {
                const updated = action.payload;
                state.templates = state.templates.map((t) => (t.id === updated.id ? { ...t, ...updated } : t));
                Toast.Success({ content: t('eval_template_restored') });
            })
            .addCase(restoreEvaluationTemplate.rejected, (_state, action: any) => {
                Toast.Error({ content: action.payload?.message || t('error') });
            })

            .addCase(deleteEvaluationTemplate.fulfilled, (state, action) => {
                state.templates = state.templates.filter((t) => t.id !== action.payload);
                Toast.Success({ content: t('eval_template_deleted') });
            })
            .addCase(deleteEvaluationTemplate.rejected, (_state, action: any) => {
                Toast.Error({ content: action.payload?.message || t('error') });
            })

            .addCase(getJobEvaluationAttachment.pending, (state) => {
                state.isLoadingJobAttachment = true;
            })
            .addCase(getJobEvaluationAttachment.fulfilled, (state, action) => {
                state.isLoadingJobAttachment = false;
                // Ignore responses that started before/during a newer upsert/delete,
                // and never clobber an in-flight write (SAB-1133).
                if (
                    action.payload.epochAtStart !== state.attachmentEpoch ||
                    state.isSavingJobAttachment
                ) {
                    return;
                }
                state.jobAttachment = action.payload.data;
            })
            .addCase(getJobEvaluationAttachment.rejected, (state) => {
                state.isLoadingJobAttachment = false;
                // Clear stale attachment from a previous job when a fetch fails
                // (404 empty state is handled as fulfilled with data: null).
                if (!state.isSavingJobAttachment) {
                    state.jobAttachment = null;
                }
            })

            .addCase(upsertJobEvaluationAttachment.pending, (state, action) => {
                state.isSavingJobAttachment = true;
                state.attachmentEpoch += 1;
                jobAttachmentRollback = state.jobAttachment;
                const requestBody = action.meta.arg?.body;
                // Optimistic move so the recruitment-stages chip updates immediately
                // even if the PUT response is slow/stale (SAB-1133).
                if (requestBody?.stageId && requestBody?.templateId) {
                    state.jobAttachment = {
                        templateId: requestBody.templateId,
                        stageId: requestBody.stageId,
                        templateName: resolveAttachmentTemplateName(state, requestBody.templateId),
                        stageName: state.jobAttachment?.stageName,
                        currentVersion: state.jobAttachment?.currentVersion || 1,
                        attachedAt: state.jobAttachment?.attachedAt || new Date().toISOString(),
                    };
                }
            })
            .addCase(upsertJobEvaluationAttachment.fulfilled, (state, action) => {
                state.isSavingJobAttachment = false;
                jobAttachmentRollback = undefined;
                // Invalidate GETs that started while the write was in flight.
                state.attachmentEpoch += 1;
                const payload = action.payload;
                const requestBody = action.meta.arg?.body;
                // Trust the request we just sent for stage/template ids — API responses
                // have been observed returning the previous stage until a hard refresh.
                const templateId = requestBody?.templateId || payload?.templateId;
                const stageId = requestBody?.stageId || payload?.stageId;
                if (!templateId || !stageId) {
                    state.jobAttachment = payload;
                    Toast.Success({ content: t('eval_attachment_saved') });
                    return;
                }
                state.jobAttachment = {
                    ...(payload || ({} as JobEvaluationAttachment)),
                    templateId,
                    stageId,
                    templateName: resolveAttachmentTemplateName(
                        state,
                        templateId,
                        payload?.templateName,
                    ),
                    stageName: payload?.stageName ?? state.jobAttachment?.stageName,
                    currentVersion: payload?.currentVersion ?? state.jobAttachment?.currentVersion ?? 1,
                    attachedAt: payload?.attachedAt || state.jobAttachment?.attachedAt || new Date().toISOString(),
                };
                Toast.Success({ content: t('eval_attachment_saved') });
            })
            .addCase(upsertJobEvaluationAttachment.rejected, (state, action: any) => {
                state.isSavingJobAttachment = false;
                state.attachmentEpoch += 1;
                if (jobAttachmentRollback !== undefined) {
                    state.jobAttachment = jobAttachmentRollback;
                    jobAttachmentRollback = undefined;
                }
                Toast.Error({ content: action.payload?.message || t('error') });
            })

            .addCase(deleteJobEvaluationAttachment.pending, (state) => {
                state.isSavingJobAttachment = true;
                state.attachmentEpoch += 1;
                jobAttachmentRollback = state.jobAttachment;
                // Optimistic clear so chips disappear immediately.
                state.jobAttachment = null;
            })
            .addCase(deleteJobEvaluationAttachment.fulfilled, (state) => {
                state.isSavingJobAttachment = false;
                jobAttachmentRollback = undefined;
                state.attachmentEpoch += 1;
                state.jobAttachment = null;
                Toast.Success({ content: t('eval_attachment_removed') });
            })
            .addCase(deleteJobEvaluationAttachment.rejected, (state, action: any) => {
                state.isSavingJobAttachment = false;
                state.attachmentEpoch += 1;
                if (jobAttachmentRollback !== undefined) {
                    state.jobAttachment = jobAttachmentRollback;
                    jobAttachmentRollback = undefined;
                }
                Toast.Error({ content: action.payload?.message || t('error') });
            })

            .addCase(listApplicationEvaluations.pending, (state) => {
                state.isLoadingEvaluations = true;
            })
            .addCase(listApplicationEvaluations.fulfilled, (state, action) => {
                state.isLoadingEvaluations = false;
                state.evaluations = action.payload;
            })
            .addCase(listApplicationEvaluations.rejected, (state, action: any) => {
                state.isLoadingEvaluations = false;
                const errorMessage = action.payload?.message;
                // Restricted roles (e.g. recruiter) get empty UI; don't flash Forbidden
                if (errorMessage === 'Forbidden') return;
                Toast.Error({ content: errorMessage || t('error') });
            })

            .addCase(getApplicationEvaluationSummary.fulfilled, (state, action) => {
                state.evaluationSummary = action.payload;
            })

            .addCase(submitEvaluation.pending, (state) => {
                state.isSubmittingEvaluation = true;
            })
            .addCase(submitEvaluation.fulfilled, (state, action) => {
                state.isSubmittingEvaluation = false;
                state.evaluations = [action.payload, ...state.evaluations.filter((e) => e.id !== action.payload.id)];
                // Keep local summary in step with the new submission (same reason as syncApplicantEvaluationSummary)
                state.evaluationSummary = {
                    latestVerdict: action.payload.verdict,
                    evaluationsCount: (state.evaluationSummary?.evaluationsCount || 0) + 1,
                    distinctEvaluators: Math.max((state.evaluationSummary?.distinctEvaluators || 0) + 1, 1),
                    myLockedEvaluationId: action.payload.id,
                };
                state.showSubmitPanel = false;
                state.editingEvaluationId = null;
                Toast.Success({ content: t('eval_submitted_success') });
            })
            .addCase(submitEvaluation.rejected, (state, action: any) => {
                state.isSubmittingEvaluation = false;
                if (action.payload?.code === 'FT-101') {
                    Toast.Error({ content: action.payload?.message || t('eval_stale_form_error') });
                    // Force re-fetch of attachment/template so the user reopens on the new version
                    state.selectedTemplate = null;
                    state.showSubmitPanel = false;
                    state.editingEvaluationId = null;
                    return;
                }
                Toast.Error({ content: action.payload?.message || t('error') });
            })

            .addCase(updateEvaluation.pending, (state) => {
                state.isSubmittingEvaluation = true;
            })
            .addCase(updateEvaluation.fulfilled, (state, action) => {
                state.isSubmittingEvaluation = false;
                state.evaluations = state.evaluations.map((e) => (e.id === action.payload.id ? action.payload : e));
                state.showSubmitPanel = false;
                state.editingEvaluationId = null;
                Toast.Success({ content: t('eval_updated_success') });
            })
            .addCase(updateEvaluation.rejected, (state, action: any) => {
                state.isSubmittingEvaluation = false;
                Toast.Error({ content: action.payload?.message || t('error') });
            })

            .addCase(deleteEvaluation.pending, (state) => {
                state.isDeletingEvaluation = true;
            })
            .addCase(deleteEvaluation.fulfilled, (state, action) => {
                state.isDeletingEvaluation = false;
                state.evaluations = state.evaluations.filter((e) => e.id !== action.payload);
                Toast.Success({ content: t('eval_deleted_success') });
            })
            .addCase(deleteEvaluation.rejected, (state, action: any) => {
                state.isDeletingEvaluation = false;
                Toast.Error({ content: action.payload?.message || t('error') });
            });
    },
});

export const {
    clearSelectedTemplate,
    setShowSubmitEvaluationPanel,
    setEditingEvaluationId,
    clearEvaluationsState,
    clearJobAttachment,
} = interviewEvaluationsSlice.actions;

export default interviewEvaluationsSlice.reducer;
